import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Switch,
  Stack,
  Chip,
  Collapse,
  IconButton,
  Link,
  Divider,
} from '@mui/material';
import {
  Cookie,
  ExpandMore,
  ExpandLess,
  Lock,
  Analytics,
  Notifications,
  OpenInNew,
  CheckCircleOutline,
} from '@mui/icons-material';
import CloseButton from '../Buttons/CloseButton';
import ActionButton, { DarkGreyButton } from '../Buttons/ActionButton';
import {
  getConsentData,
  acceptAllCookies,
  rejectOptionalCookies,
  saveConsentPreferences,
} from '../../../utils/cookieUtils';
import { initPostHog } from '../../../hooks/usePostHog';
import { CANNY_ENABLED, initCanny } from '../../../hooks/useCanny';
import { removeAnalyticsScripts, removePostHogScripts, removeCannyScripts } from '../../../utils/scriptLoadingUtils';
import { toast } from 'react-toastify';

const CANNY_APP_ID = process.env.REACT_APP_ENV_NAME === 'prod'
  ? '688780adbe67c13d05c1470e'
  : '6863bbdcd31852b4e2d4d488';

const SUCCESS_MSG = 'Cookie Preferences saved successfully';

const switchStyle = {
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#AF3241',
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#AF3241',
  },
};

const CookieCategory = ({
  icon,
  title,
  description,
  isRequired,
  isEnabled,
  onToggle,
  expanded,
  onExpand,
  details,
  disabled,
}) => (
  <Box
    sx={{
      border: '1px solid #e9ecef',
      borderRadius: 2,
      overflow: 'hidden',
      backgroundColor: '#fff',
    }}
  >
    <Box
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        backgroundColor: expanded ? '#fafafa' : '#fff',
        transition: 'background-color 0.2s',
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: isRequired ? '#e8e8e8' : '#fff3f4',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
            {title}
          </Typography>
          {isRequired && (
            <Chip
              label="Required"
              size="small"
              sx={{
                height: 20,
                fontSize: '11px',
                backgroundColor: '#e8e8e8',
                color: '#666',
              }}
            />
          )}
        </Stack>
        <Typography variant="body2" sx={{ color: '#666', fontSize: '13px' }}>
          {description}
        </Typography>
      </Box>

      <Stack direction="row" alignItems="center" spacing={1}>
        {isRequired ? (
          <Chip
            icon={<CheckCircleOutline sx={{ fontSize: 14 }} />}
            label="Always On"
            size="small"
            sx={{
              backgroundColor: '#e8f5e9',
              color: '#2e7d32',
              '& .MuiChip-icon': { color: '#2e7d32' },
            }}
          />
        ) : (
          <Switch
            checked={isEnabled}
            onChange={onToggle}
            disabled={disabled}
            sx={switchStyle}
          />
        )}
        <IconButton size="small" onClick={onExpand}>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Stack>
    </Box>

    <Collapse in={expanded}>
      <Box sx={{ px: 2, pb: 2, pt: 1, backgroundColor: '#fafafa' }}>
        <Divider sx={{ mb: 2 }} />
        {details}
      </Box>
    </Collapse>
  </Box>
);

const CookieDetailModal = ({ open, onClose }) => {
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [initialAnalytics, setInitialAnalytics] = useState(false);
  const [initialNotifications, setInitialNotifications] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (open) {
      const consentData = getConsentData();
      const analytics = consentData?.analytics === true;
      const notifications = consentData?.notifications === true;

      setAnalyticsEnabled(analytics);
      setNotificationsEnabled(notifications);
      setInitialAnalytics(analytics);
      setInitialNotifications(notifications);
    }
  }, [open]);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleAnalyticsToggle = (event) => {
    setAnalyticsEnabled(event.target.checked);
  };

  const handleNotificationsToggle = (event) => {
    setNotificationsEnabled(event.target.checked);
  };

  const applyConsentChanges = async (analytics, notifications) => {
    if (analytics && !initialAnalytics) {
      await initPostHog();
    } else if (!analytics && initialAnalytics) {
      removePostHogScripts();
    }

    // Do not initialize Canny while it is temporarily disabled.
    if (CANNY_ENABLED && notifications && !initialNotifications) {
      await initCanny(CANNY_APP_ID);
    } else if (!notifications && initialNotifications) {
      removeCannyScripts();
    }
  };

  const handleAcceptAll = async () => {
    setIsUpdating(true);
    acceptAllCookies();

    try {
      // Canny init is skipped while CANNY_ENABLED is false; PostHog is unchanged.
      const inits = [initPostHog()];
      if (CANNY_ENABLED) {
        inits.push(initCanny(CANNY_APP_ID));
      }
      await Promise.allSettled(inits);
    } catch {
      // Silent fail
    } finally {
      setIsUpdating(false);
      toast(SUCCESS_MSG, { type: 'success' });
      if (onClose) onClose(true);
    }
  };

  const handleRejectAll = () => {
    rejectOptionalCookies();
    removeAnalyticsScripts();
    toast(SUCCESS_MSG, { type: 'success' });
    if (onClose) onClose(true);
  };

  const handleSavePreferences = async () => {
    setIsUpdating(true);

    try {
      saveConsentPreferences(analyticsEnabled, notificationsEnabled);
      await applyConsentChanges(analyticsEnabled, notificationsEnabled);
      toast(SUCCESS_MSG, { type: 'success' });
      if (onClose) onClose(true);
    } catch {
      toast('Error saving preferences', { type: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDismiss = () => {
    if (onClose) onClose(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleDismiss}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: '#fff3f4',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Cookie sx={{ color: '#AF3241', fontSize: 20 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Cookie Settings
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
            We use cookies and similar technologies to provide essential functionality and improve your experience.
            You can customize your preferences below.
          </Typography>

          <CookieCategory
            icon={<Lock sx={{ color: '#666', fontSize: 20 }} />}
            title="Necessary Cookies"
            description="Required for authentication, session management, and core functionality."
            isRequired={true}
            isEnabled={true}
            expanded={expandedSection === 'necessary'}
            onExpand={() => toggleSection('necessary')}
            details={
              <Stack spacing={2}>
                <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.6 }}>
                  These cookies are essential for the application to function properly. They enable
                  secure authentication, workspace management, and role-based access control.
                </Typography>
                <Box sx={{ backgroundColor: '#fff', p: 2, borderRadius: 1, border: '1px solid #e9ecef' }}>
                  <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1.5 }}>
                    COOKIES USED
                  </Typography>
                  <Stack spacing={1.5}>
                    {[
                      { name: 'authToken', desc: 'Encrypted JWT token for secure API authentication • Session' },
                      { name: 'userDetail', desc: 'User profile information (name, email, role, settings) • Session' },
                      { name: 'userPermissions', desc: 'Feature-level access permissions for your role • Session' },
                      { name: 'current_workspace', desc: 'Currently selected workspace context • Session' },
                      { name: 'workspace_options', desc: 'List of workspaces you have access to • Session' },
                      { name: 'access_types', desc: 'Defines your access type (customer, brand, internal) • Session' },
                      { name: 'cookie_consent', desc: 'Stores your cookie preferences • Persistent' },
                    ].map(({ name, desc }) => (
                      <Box key={name}>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '13px' }}>
                          {name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          {desc}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            }
          />

          <CookieCategory
            icon={<Analytics sx={{ color: '#AF3241', fontSize: 20 }} />}
            title="Analytics Cookies"
            description="Help us understand how you use the application to improve our service."
            isRequired={false}
            isEnabled={analyticsEnabled}
            onToggle={handleAnalyticsToggle}
            disabled={isUpdating}
            expanded={expandedSection === 'analytics'}
            onExpand={() => toggleSection('analytics')}
            details={
              <Stack spacing={2}>
                <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.6 }}>
                  We use PostHog to collect anonymous usage data that helps us understand how our
                  application is used and identify areas for improvement.
                </Typography>

                <Box sx={{ backgroundColor: '#fff', p: 2, borderRadius: 1, border: '1px solid #e9ecef' }}>
                  <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1.5 }}>
                    PROVIDED BY
                  </Typography>
                  <Stack direction="row" alignItems="center">
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                        PostHog
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Product Analytics Platform
                      </Typography>
                    </Box>
                    <Link
                      href="https://posthog.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        marginLeft: 'auto',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontSize: '12px',
                        px: 1,
                        py: 0.5,
                        mt: -2.5,
                        borderRadius: 1,
                        color: '#AF3241',
                        backgroundColor: 'rgba(175,50,65,0.08)',
                        textDecoration: 'none',
                        '&:hover': { backgroundColor: 'rgba(175,50,65,0.15)', textDecoration: 'none' },
                      }}
                    >
                      Privacy Policy <OpenInNew sx={{ fontSize: 12 }} />
                    </Link>
                  </Stack>
                </Box>

                <Box sx={{ backgroundColor: '#fff', p: 2, borderRadius: 1, border: '1px solid #e9ecef' }}>
                  <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                    DATA COLLECTED
                  </Typography>
                  <Stack spacing={0.5}>
                    {[
                      'Page views and navigation patterns',
                      'Feature usage and interaction events',
                      'Device and browser information',
                      'Session duration and frequency',
                      'Session recordings of user interactions (inputs are masked)',
                    ].map((item) => (
                      <Typography key={item} variant="body2" sx={{ fontSize: '13px', color: '#555' }}>
                        • {item}
                      </Typography>
                    ))}
                  </Stack>
                </Box>

                <Box sx={{ backgroundColor: '#e8f5e9', p: 2, borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: '#2e7d32', display: 'block', mb: 0.5, fontWeight: 600 }}>
                    PRIVACY COMMITMENT
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '13px', color: '#1b5e20' }}>
                    Data is hosted in the EU (GDPR compliant). All form inputs in session recordings
                    are masked. We do not sell data to third parties or use data for advertising purposes.
                  </Typography>
                </Box>
              </Stack>
            }
          />

          {/* Hide Canny notification consent (and the canny.io privacy link) while disabled. */}
          {CANNY_ENABLED && (
          <CookieCategory
            icon={<Notifications sx={{ color: '#AF3241', fontSize: 20 }} />}
            title="Notification Cookies"
            description="Enable in-app notifications about new features, updates, and improvements."
            isRequired={false}
            isEnabled={notificationsEnabled}
            onToggle={handleNotificationsToggle}
            disabled={isUpdating}
            expanded={expandedSection === 'notifications'}
            onExpand={() => toggleSection('notifications')}
            details={
              <Stack spacing={2}>
                <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.6 }}>
                  We use Canny to deliver changelog notifications and keep you informed about new
                  features, improvements, and important updates to CAP Explorer.
                </Typography>

                <Box sx={{ backgroundColor: '#fff', p: 2, borderRadius: 1, border: '1px solid #e9ecef' }}>
                  <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1.5 }}>
                    PROVIDED BY
                  </Typography>
                  <Stack direction="row" alignItems="center">
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                        Canny
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Product Feedback & Changelog Platform
                      </Typography>
                    </Box>
                    <Link
                      href="https://canny.io/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        marginLeft: 'auto',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontSize: '12px',
                        px: 1,
                        py: 0.5,
                        mt: -2.5,
                        borderRadius: 1,
                        color: '#AF3241',
                        backgroundColor: 'rgba(175,50,65,0.08)',
                        textDecoration: 'none',
                        '&:hover': { backgroundColor: 'rgba(175,50,65,0.15)', textDecoration: 'none' },
                      }}
                    >
                      Privacy Policy <OpenInNew sx={{ fontSize: 12 }} />
                    </Link>
                  </Stack>
                </Box>

                <Box sx={{ backgroundColor: '#fff', p: 2, borderRadius: 1, border: '1px solid #e9ecef' }}>
                  <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                    DATA COLLECTED
                  </Typography>
                  <Stack spacing={0.5}>
                    {[
                      'Changelog view status (read/unread)',
                      'Last seen notification timestamp',
                    ].map((item) => (
                      <Typography key={item} variant="body2" sx={{ fontSize: '13px', color: '#555' }}>
                        • {item}
                      </Typography>
                    ))}
                  </Stack>
                </Box>

                <Box sx={{ backgroundColor: '#e8f5e9', p: 2, borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: '#2e7d32', display: 'block', mb: 0.5, fontWeight: 600 }}>
                    PRIVACY COMMITMENT
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '13px', color: '#1b5e20' }}>
                    Canny only stores minimal data required to track notification status.
                    No personal data is shared or used for marketing purposes.
                  </Typography>
                </Box>
              </Stack>
            }
          />
          )}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          '& > *': { m: '0 !important' },
        }}
      >
        <CloseButton onClick={handleRejectAll} label="Reject All" />
        <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
        <DarkGreyButton
          onClick={handleAcceptAll}
          label="Accept All"
          disabled={isUpdating}
          sx={{ mt: 0, mb: 0 }}
        />
        <ActionButton
          onClick={handleSavePreferences}
          label="Save Preferences"
          disabled={isUpdating}
          sx={{ mt: 0, mb: 0 }}
        />
      </DialogActions>
    </Dialog>
  );
};

export default CookieDetailModal;